import axios from 'axios';
import API_CONFIG from '../config/api';
import { getAuthHeaders } from '../utils/auth';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Server responded with error status
      console.error('API Error:', error.response.data);
    } else if (error.request) {
      // Request made but no response received
      console.error('Network Error:', error.message);
    } else {
      // Something else happened
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

const blogService = {
  /**
   * Get all blogs with optional filters
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {string} params.category - Filter by category
   * @param {string} params.search - Search term
   * @returns {Promise} Blog data with pagination
   */
  getAllBlogs: async (params = {}) => {
    try {
      const response = await apiClient.get('/blogs', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get blog by ID
   * @param {number} id - Blog ID
   * @returns {Promise} Blog data
   */
  getBlogById: async (id) => {
    try {
      const response = await apiClient.get(`/blogs/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get blog by slug
   * @param {string} slug - Blog slug
   * @returns {Promise} Blog data
   */
  getBlogBySlug: async (slug) => {
    try {
      const response = await apiClient.get(`/blogs/slug/${slug}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get all categories
   * @returns {Promise} Array of categories
   */
  getCategories: async () => {
    try {
      const response = await apiClient.get('/blogs/categories');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get comments for a blog
   * @param {number} blogId - The ID of the blog
   * @returns {Promise} Array of comments
   */
  getComments: async (blogId) => {
    try {
      const response = await apiClient.get(`/blogs/${blogId}/comments`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Create new comment for a blog
   * @param {number} blogId - The ID of the blog
   * @param {Object} commentData - The comment data
   * @returns {Promise} Created comment
   */
  createComment: async (blogId, commentData) => {
    try {
      const response = await apiClient.post(`/blogs/${blogId}/comments`, commentData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Create new blog (requires authentication)
   * @param {Object} blogData - Blog data
   * @returns {Promise} Created blog
   */
  createBlog: async (blogData) => {
    try {
      const response = await apiClient.post('/blogs', blogData, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Update blog (requires authentication)
   * @param {number} id - Blog ID
   * @param {Object} blogData - Updated blog data
   * @returns {Promise} Updated blog
   */
  updateBlog: async (id, blogData) => {
    try {
      const response = await apiClient.put(`/blogs/${id}`, blogData, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Delete blog (requires authentication)
   * @param {number} id - Blog ID
   * @returns {Promise} Success message
   */
  deleteBlog: async (id) => {
    try {
      const response = await apiClient.delete(`/blogs/${id}`, {
        headers: getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Increment comment count
   * @param {number} id - Blog ID
   * @returns {Promise} Success message
   */
  incrementComments: async (id) => {
    try {
      const response = await apiClient.post(`/blogs/${id}/comments/increment`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Get total count of blogs
   * @returns {Promise<number>} Total number of blogs
   */
  getBlogCount: async () => {
    try {
      const response = await apiClient.get('/blogs', { params: { limit: 1 } });
      // Check pagination.total first, then fall back to data array length
      return response.data.pagination?.total || response.data.data?.length || 0;
    } catch (error) {
      console.error('Error fetching blog count:', error);
      return 0;
    }
  },
};

export default blogService;

